import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  openGraph?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
    locale?: string;
    siteName?: string;
    profile?: {
      first_name?: string;
      last_name?: string;
      gender?: string;
    };
  };
  twitter?: {
    card?: string;
    title?: string;
    description?: string;
    image?: string;
    creator?: string;
  };
  structuredData?: any[];
}

export interface SEOUpdate {
  updateSEO: (config: SEOConfig) => void;
}

// SEO configurations for different pages
const getSEOConfig = (pathname: string, language: string): SEOConfig => {
  const isZh = language === 'zh';
  
  const configs: Record<string, SEOConfig> = {
    '/': {
      title: isZh ? 'Resonow - 专业疗愈师平台' : 'Resonow - Professional Healing Platform',
      description: isZh 
        ? 'Resonow是一个专业的疗愈师平台，提供能量疗愈、心理咨询、传统中医等多种疗愈服务。找到适合您的疗愈师，开始您的康复之旅。'
        : 'Resonow is a professional healing platform offering energy healing, psychological counseling, traditional Chinese medicine, and various healing services. Find your perfect healer and start your healing journey.',
      keywords: isZh 
        ? '疗愈师, 能量疗愈, 心理咨询, 传统中医, 瑜伽, 冥想, 芳香疗法'
        : 'healer, energy healing, psychological counseling, traditional chinese medicine, yoga, meditation, aromatherapy',
      openGraph: {
        title: isZh ? 'Resonow - 专业疗愈师平台' : 'Resonow - Professional Healing Platform',
        description: isZh 
          ? '找到专业认证的疗愈师，体验个性化的疗愈服务'
          : 'Find certified professional healers and experience personalized healing services',
        type: 'website'
      }
    },
    '/book-an-appointment': {
      title: isZh ? '预约疗愈师 - Resonow' : 'Book a Healer - Resonow',
      description: isZh 
        ? '在Resonow平台上预约专业疗愈师。浏览不同专业的疗愈师，查看评价，选择适合您的疗愈方式。'
        : 'Book professional healers on Resonow platform. Browse healers from different specialties, read reviews, and choose the healing approach that suits you.',
      keywords: isZh 
        ? '预约疗愈师, 在线预约, 疗愈预约, 专业疗愈师'
        : 'book healer, online booking, healing appointment, professional healers',
      openGraph: {
        title: isZh ? '预约专业疗愈师 - Resonow' : 'Book Professional Healers - Resonow',
        description: isZh 
          ? '立即预约认证的专业疗愈师，开始您的疗愈之旅'
          : 'Book certified professional healers now and start your healing journey',
        type: 'website'
      }
    },
    '/certifications': {
      title: isZh ? '疗愈师认证 - Resonow' : 'Healer Certifications - Resonow',
      description: isZh 
        ? '了解Resonow平台疗愈师的专业认证和疗愈阶段。探索不同的疗愈方式，找到适合您当前状态的服务。'
        : 'Learn about professional certifications and healing stages on Resonow platform. Explore different healing approaches and find services that match your current state.',
      keywords: isZh 
        ? '疗愈师认证, 专业认证, 疗愈阶段, 能量疗愈认证, 心理咨询认证'
        : 'healer certification, professional certification, healing stages, energy healing certification, psychological counseling certification',
      openGraph: {
        title: isZh ? '专业疗愈师认证体系 - Resonow' : 'Professional Healer Certification System - Resonow',
        description: isZh 
          ? '了解我们严格的认证体系，确保疗愈服务的专业性和安全性'
          : 'Learn about our rigorous certification system ensuring professional and safe healing services',
        type: 'website'
      }
    },
    '/about-us': {
      title: isZh ? '关于我们 - Resonow' : 'About Us - Resonow',
      description: isZh 
        ? '了解Resonow的使命和愿景。我们致力于连接专业疗愈师和寻求疗愈的人们，提供安全、专业的疗愈服务平台。'
        : 'Learn about Resonow\'s mission and vision. We are dedicated to connecting professional healers with those seeking healing, providing a safe and professional healing service platform.',
      keywords: isZh 
        ? '关于Resonow, 疗愈平台, 使命愿景, 专业疗愈'
        : 'about resonow, healing platform, mission vision, professional healing',
      openGraph: {
        title: isZh ? '关于Resonow - 专业疗愈师平台' : 'About Resonow - Professional Healing Platform',
        description: isZh 
          ? '了解Resonow的故事和我们对疗愈行业的承诺'
          : 'Learn about Resonow\'s story and our commitment to the healing industry',
        type: 'website'
      }
    }
  };

  return configs[pathname] || configs['/'];
};

export const useSEO = (customConfig?: Partial<SEOConfig>): SEOUpdate => {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const updateSEO = (config: SEOConfig) => {
    // Update document title
    document.title = config.title;
    
    // Update meta tags
    updateMetaTag('description', config.description);
    if (config.keywords) {
      updateMetaTag('keywords', config.keywords);
    }
    
    // Update canonical URL
    const canonical = config.canonical || `${window.location.origin}${location.pathname}`;
    updateCanonicalTag(canonical);
    
    // Update Open Graph tags
    if (config.openGraph) {
      updateMetaTag('og:title', config.openGraph.title || config.title);
      updateMetaTag('og:description', config.openGraph.description || config.description);
      updateMetaTag('og:type', config.openGraph.type || 'website');
      updateMetaTag('og:url', config.openGraph.url || canonical);
      updateMetaTag('og:site_name', config.openGraph.siteName || 'Resonow');
      updateMetaTag('og:locale', config.openGraph.locale || i18n.language);
      
      if (config.openGraph.image) {
        updateMetaTag('og:image', config.openGraph.image);
      }
      
      if (config.openGraph.profile) {
        updateMetaTag('profile:first_name', config.openGraph.profile.first_name || '');
        updateMetaTag('profile:last_name', config.openGraph.profile.last_name || '');
        updateMetaTag('profile:gender', config.openGraph.profile.gender || '');
      }
    }
    
    // Update Twitter Card tags
    if (config.twitter) {
      updateMetaTag('twitter:card', config.twitter.card || 'summary_large_image');
      updateMetaTag('twitter:title', config.twitter.title || config.title);
      updateMetaTag('twitter:description', config.twitter.description || config.description);
      updateMetaTag('twitter:creator', config.twitter.creator || '');
      
      if (config.twitter.image) {
        updateMetaTag('twitter:image', config.twitter.image);
      }
    }
    
    // Update language tags
    updateMetaTag('language', i18n.language);
    updateHtmlLang(i18n.language);
    
    // Add/Update JSON-LD structured data
    if (config.structuredData) {
      updateJsonLd(config.structuredData);
    }
  };
  
  useEffect(() => {
    try {
      const currentLanguage = i18n.resolvedLanguage || i18n.language || 'en';
      const defaultConfig = getSEOConfig(location.pathname, currentLanguage);
      const finalConfig = { ...defaultConfig, ...customConfig };
      updateSEO(finalConfig);
    } catch (error) {
      console.warn('Error updating SEO:', error);
    }
  }, [location.pathname, i18n.language, customConfig]);
  
  return { updateSEO };
};

// Helper functions
const updateMetaTag = (property: string, content: string) => {
  const selector = property.startsWith('og:') || property.startsWith('twitter:') 
    ? `meta[property="${property}"]` 
    : `meta[name="${property}"]`;
  
  let element = document.querySelector(selector) as HTMLMetaElement;
  
  if (!element) {
    element = document.createElement('meta');
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      element.setAttribute('property', property);
    } else {
      element.setAttribute('name', property);
    }
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
};

const updateCanonicalTag = (url: string) => {
  let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', url);
};

const updateHtmlLang = (lang: string) => {
  document.documentElement.setAttribute('lang', lang);
};

const updateJsonLd = (data: any) => {
  // Remove existing JSON-LD
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }
  
  // Add new JSON-LD
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};